/**
 * Tracks whether a touch or pen press happened moments ago, so hover-driven
 * popups (Tooltip, HoverCard) can veto opens that were synthesized FROM a tap
 * rather than performed by a hovering pointer.
 *
 * Why event-level and not device-level: a `(hover: none)` device gate throws
 * out interactions that other input modalities on the same device perform
 * legitimately — an iPad with a Magic Keyboard trackpad hovers with a real
 * mouse pointer, and keyboard focus opens tooltips via focus-visible. The
 * only opens that must die are the tap-synthesized ones: WebKit dispatches
 * compatibility mouse events after a tap (carrying no pointer type for hover
 * guards to key on), and pencil presses count as mouse-like in Base UI's
 * hover check — both open a popup that no hover-end event will ever dismiss.
 * A real hover never begins with a touch/pen press, so "did one land within
 * the last moment" cleanly separates the two. (A hover-capable pencil
 * hovering above the screen fires no press, and is a real hover.)
 */

const RECENT_PRESS_WINDOW_MS = 700;

let lastTouchOrPenPressAt = -Infinity;
let installed = false;

function recordPress(event: PointerEvent) {
  if (event.pointerType === "touch" || event.pointerType === "pen") {
    lastTouchOrPenPressAt = performance.now();
  }
}

/**
 * Installs the global press listeners once. Idempotent and cheap — callers
 * invoke it during render so the listeners exist before any interaction.
 * Both down and up are recorded: compatibility mouse events fire after the
 * pointer lifts, which for a long-press can be arbitrarily far from the down.
 */
export function ensureRecentTapTracking(): void {
  if (installed || typeof window === "undefined") {
    return;
  }

  installed = true;
  window.addEventListener("pointerdown", recordPress, {
    capture: true,
    passive: true,
  });
  window.addEventListener("pointerup", recordPress, {
    capture: true,
    passive: true,
  });
}

/** Whether a touch/pen press landed within the last moment (~700ms). */
export function wasRecentTouchOrPenPress(): boolean {
  return performance.now() - lastTouchOrPenPressAt < RECENT_PRESS_WINDOW_MS;
}
